import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Scan, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // 'loading' | 'success' | 'error'
  const [message, setMessage] = useState('');

  useEffect(() => {
    const token = searchParams.get('token');
    if (!token) {
      setStatus('error');
      setMessage('No se encontró el token de verificación en la URL.');
      return;
    }
    axios.get(`${API_URL}/api/auth/verify-email?token=${token}`)
      .then(res => {
        setStatus('success');
        setMessage(res.data.message || 'Email verificado correctamente');
      })
      .catch(err => {
        setStatus('error');
        setMessage(err.response?.data?.detail || 'Error al verificar el email. El enlace puede haber caducado.');
      });
  }, [searchParams]);

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center px-4 relative">
      <div className="scanlines" />
      <div className="fixed inset-0 pointer-events-none" style={{ background: 'radial-gradient(circle at 50% 0%, rgba(59, 130, 246, 0.08), transparent 60%)' }} />

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="relative z-10 w-full max-w-md"
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <button onClick={() => navigate('/')} className="inline-flex items-center gap-3 mb-2 hover:opacity-80 transition-opacity">
            <Scan className="w-8 h-8 text-blue-500" />
            <span className="font-heading text-2xl font-black">VERITAS <span className="text-blue-500">SCAN</span></span>
          </button>
        </div>

        <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-8 text-center space-y-6 shadow-2xl">
          {status === 'loading' && (
            <>
              <div className="w-16 h-16 mx-auto rounded-full bg-blue-500/10 border border-blue-500/30 flex items-center justify-center">
                <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
              </div>
              <div>
                <h1 className="font-heading text-2xl font-black text-white mb-2">Verificando...</h1>
                <p className="text-zinc-500 text-sm font-mono">Comprobando tu enlace de verificación</p>
              </div>
            </>
          )}

          {status === 'success' && (
            <>
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', damping: 12, delay: 0.1 }}
                className="w-20 h-20 mx-auto rounded-full bg-emerald-500/10 border-2 border-emerald-500/40 flex items-center justify-center"
              >
                <CheckCircle className="w-10 h-10 text-emerald-500" />
              </motion.div>
              <div>
                <h1 className="font-heading text-2xl font-black text-white mb-2">¡Email verificado!</h1>
                <p className="text-zinc-400 text-sm leading-relaxed">
                  Tu cuenta de Veritas Scan ha sido verificada correctamente. Ya puedes iniciar sesión y acceder a todas las funcionalidades.
                </p>
              </div>
              <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-xl p-4">
                <p className="text-emerald-400 text-sm font-mono">{message}</p>
              </div>
              <button
                onClick={() => navigate('/')}
                className="w-full py-3 bg-blue-600 hover:bg-blue-500 text-white font-mono uppercase tracking-wider rounded-xl transition-all font-bold shadow-[0_0_30px_rgba(59,130,246,0.2)]"
              >
                Ir a Veritas Scan →
              </button>
            </>
          )}

          {status === 'error' && (
            <>
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', damping: 12, delay: 0.1 }}
                className="w-20 h-20 mx-auto rounded-full bg-red-500/10 border-2 border-red-500/30 flex items-center justify-center"
              >
                <XCircle className="w-10 h-10 text-red-500" />
              </motion.div>
              <div>
                <h1 className="font-heading text-2xl font-black text-white mb-2">Enlace inválido</h1>
                <p className="text-zinc-400 text-sm leading-relaxed">
                  El enlace de verificación ha caducado o ya fue utilizado. Puedes solicitar uno nuevo desde tu perfil.
                </p>
              </div>
              <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-4">
                <p className="text-red-400 text-sm font-mono">{message}</p>
              </div>
              <div className="flex flex-col gap-3">
                <button
                  onClick={() => navigate('/')}
                  className="w-full py-3 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-zinc-300 font-mono text-sm uppercase tracking-wider rounded-xl transition-all"
                >
                  Ir al inicio
                </button>
              </div>
            </>
          )}
        </div>

        <p className="text-center text-zinc-600 text-xs font-mono mt-6">
          ¿Problemas? Escríbenos a{' '}
          <a href="mailto:soporte@veritas-scan.com" className="text-blue-400 hover:text-blue-300 transition-colors">
            soporte@veritas-scan.com
          </a>
        </p>
      </motion.div>
    </div>
  );
}
